import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  LinearProgress,
  Avatar,
  InputAdornment,
  Menu,
  Autocomplete
} from '@mui/material';
import {
  CreditCard as BillingIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Payment as PaymentIcon,
  TrendingUp as GrowthIcon,
  TrendingDown as ChurnIcon,
  AttachMoney as RevenueIcon,
  CalendarMonth as RecurringIcon,
  Warning as WarningIcon,
  CheckCircle as ActiveIcon,
  Error as PastDueIcon,
  Schedule as ScheduleIcon,
  Receipt as InvoiceIcon,
  CardGiftcard as DiscountIcon,
  MoreVert as MoreIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Person as CustomerIcon,
  LocationOn as LocationIcon,
  TrendingUp,
  TrendingDown
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`billing-tabpanel-${index}`}
      aria-labelledby={`billing-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const BillingPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  
  // Dialog states
  const [subscriptionDialog, setSubscriptionDialog] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<any>(null);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [pauseDialog, setPauseDialog] = useState(false);
  const [discountDialog, setDiscountDialog] = useState(false);
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  
  // Form states
  const [subscriptionForm, setSubscriptionForm] = useState({
    customer: null as any,
    location: '',
    type: 'Monthly Site',
    name: '',
    description: '',
    site: '',
    billing: {
      amount: 0,
      interval: 'monthly',
      intervalCount: 1,
      taxRate: 0
    },
    payment: {
      method: 'card',
      autopay: {
        enabled: true
      }
    },
    schedule: {
      startDate: dayjs(),
      endDate: null as any
    },
    trial: {
      enabled: false,
      days: 0
    }
  });

  const [cancelForm, setCancelForm] = useState({
    reason: '',
    feedback: '',
    effectiveDate: null as any
  });

  const [pauseForm, setPauseForm] = useState({
    until: null as any,
    reason: ''
  });

  const [discountForm, setDiscountForm] = useState({
    type: 'percentage',
    value: 0,
    reason: '',
    endDate: null as any
  });

  useEffect(() => {
    fetchSubscriptions();
    fetchAnalytics();
  }, [page, rowsPerPage, statusFilter, typeFilter, locationFilter]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      });
      
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('type', typeFilter);
      if (locationFilter) params.append('locationId', locationFilter);
      
      const response = await api.get(`/billing/subscriptions?${params}`);
      
      setSubscriptions(response.data.subscriptions);
      setTotalSubscriptions(response.data.totalSubscriptions);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 1);
      
      const response = await api.get('/billing/analytics', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleCreateSubscription = () => {
    setSelectedSubscription(null);
    setSubscriptionForm({
      customer: null,
      location: '',
      type: 'Monthly Site',
      name: '',
      description: '',
      site: '',
      billing: {
        amount: 0,
        interval: 'monthly',
        intervalCount: 1,
        taxRate: 0
      },
      payment: {
        method: 'card',
        autopay: {
          enabled: true
        }
      },
      schedule: {
        startDate: dayjs(),
        endDate: null
      },
      trial: {
        enabled: false,
        days: 0
      }
    });
    setSubscriptionDialog(true);
  };

  const handleSaveSubscription = async () => {
    try {
      const data = {
        ...subscriptionForm,
        schedule: {
          ...subscriptionForm.schedule,
          startDate: subscriptionForm.schedule.startDate.toISOString(),
          endDate: subscriptionForm.schedule.endDate?.toISOString()
        }
      };

      if (selectedSubscription) {
        await api.put(`/billing/subscriptions/${selectedSubscription._id}`, data);
      } else {
        await api.post('/billing/subscriptions', data);
      }
      
      setSubscriptionDialog(false);
      fetchSubscriptions();
    } catch (error) {
      console.error('Error saving subscription:', error);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await api.post(`/billing/subscriptions/${selectedSubscription._id}/cancel`, {
        ...cancelForm,
        effectiveDate: cancelForm.effectiveDate?.toISOString()
      });
      
      setCancelDialog(false);
      fetchSubscriptions();
    } catch (error) {
      console.error('Error canceling subscription:', error);
    }
  };

  const handlePauseSubscription = async () => {
    try {
      await api.post(`/billing/subscriptions/${selectedSubscription._id}/pause`, {
        until: pauseForm.until?.toISOString(),
        reason: pauseForm.reason
      });
      
      setPauseDialog(false);
      fetchSubscriptions();
    } catch (error) {
      console.error('Error pausing subscription:', error);
    }
  };

  const handleResumeSubscription = async (subscriptionId: string) => {
    try {
      await api.post(`/billing/subscriptions/${subscriptionId}/resume`, {});
      fetchSubscriptions();
    } catch (error) {
      console.error('Error resuming subscription:', error);
    }
  };

  const handleApplyDiscount = async () => {
    try {
      await api.post(`/billing/subscriptions/${selectedSubscription._id}/discount`, {
        ...discountForm,
        endDate: discountForm.endDate?.toISOString()
      });
      
      setDiscountDialog(false);
      fetchSubscriptions();
    } catch (error) {
      console.error('Error applying discount:', error);
    }
  };

  const handleProcessBilling = async () => {
    if (window.confirm('Are you sure you want to process all due subscriptions?')) {
      try {
        const response = await api.post('/billing/process-due', {});
        
        alert(`Billing completed: ${response.data.results?.succeeded} succeeded, ${response.data.results?.failed} failed`);
        fetchSubscriptions();
      } catch (error) {
        console.error('Error processing billing:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      'active': 'success',
      'pending': 'info',
      'past_due': 'error',
      'canceled': 'default',
      'suspended': 'error',
      'expired': 'default',
      'trialing': 'info',
      'paused': 'warning'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <ActiveIcon />;
      case 'past_due':
        return <PastDueIcon />;
      case 'paused':
        return <PauseIcon />;
      case 'canceled':
        return <CancelIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const formatInterval = (interval: string, count: number) => {
    if (!interval || !count) return 'N/A';
    const label = count > 1 ? `Every ${count} ${interval}s` : `${interval}ly`;
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const renderMetricCard = (title: string, value: any, subtitle?: string, icon?: React.ReactNode, trend?: string) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4">
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            {trend === 'up' ? <TrendingUp color="success" /> : <TrendingDown color="error" />}
            <Typography variant="body2" color={trend === 'up' ? 'success.main' : 'error.main'}>
              {trend === 'up' ? 'Increasing' : 'Decreasing'}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Recurring Billing & Subscriptions
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleProcessBilling}
            >
              Process Due
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateSubscription}
            >
              New Subscription
            </Button>
          </Stack>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage recurring billing, subscriptions, and automated charges
        </Typography>
      </Box>

      {/* Analytics Overview */}
      {analytics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            {renderMetricCard(
              'Monthly Recurring Revenue',
              `$${analytics.mrr?.toFixed(2) || 0}`,
              'Current MRR',
              <RevenueIcon color="primary" />,
              analytics.growth?.mrrGrowth > 0 ? 'up' : 'down'
            )}
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            {renderMetricCard(
              'Active Subscriptions',
              analytics.summary?.activeCount || 0,
              `${analytics.summary?.totalSubscriptions || 0} total`,
              <RecurringIcon color="success" />
            )}
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            {renderMetricCard(
              'Churn Rate',
              `${analytics.churnRate || 0}%`,
              'Last 30 days',
              <ChurnIcon color="error" />
            )}
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            {renderMetricCard(
              'Customer LTV',
              `$${analytics.ltv || 0}`,
              'Average lifetime value',
              <CustomerIcon color="info" />
            )}
          </Grid>
        </Grid>
      )}

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Subscriptions" icon={<BillingIcon />} iconPosition="start" />
          <Tab label="Analytics" icon={<GrowthIcon />} iconPosition="start" />
          <Tab label="Invoices" icon={<InvoiceIcon />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Subscriptions Tab */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="past_due">Past Due</MenuItem>
                <MenuItem value="canceled">Canceled</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
                <MenuItem value="trialing">Trialing</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Seasonal Site">Seasonal Site</MenuItem>
                <MenuItem value="Monthly Site">Monthly Site</MenuItem>
                <MenuItem value="Storage">Storage</MenuItem>
                <MenuItem value="Membership">Membership</MenuItem>
                <MenuItem value="WiFi Service">WiFi Service</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Billing</TableCell>
                  <TableCell>Next Billing</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : subscriptions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No subscriptions found
                    </TableCell>
                  </TableRow>
                ) : (
                  subscriptions.map((subscription) => (
                    <TableRow key={subscription._id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                            {subscription.customer?.firstName?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {subscription.customer?.firstName} {subscription.customer?.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {subscription.customer?.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {subscription.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {subscription.type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(subscription.status)}
                          label={subscription.status}
                          color={getStatusColor(subscription.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        ${subscription.billing?.totalAmount?.toFixed(2) || '0.00'}
                      </TableCell>
                      <TableCell>
                        {formatInterval(subscription.billing?.interval, subscription.billing?.intervalCount)}
                      </TableCell>
                      <TableCell>
                        {subscription.schedule?.nextBillingDate
                          ? dayjs(subscription.schedule.nextBillingDate).format('MMM D, YYYY')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<LocationIcon />}
                          label={subscription.location?.code || 'N/A'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setSelectedSubscription(subscription);
                            setAnchorEl(e.currentTarget);
                          }}
                        >
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={totalSubscriptions}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Analytics Tab */}
          {analytics && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Subscription Breakdown by Type
                  </Typography>
                  <List>
                    {analytics.byType?.map((type: any) => (
                      <ListItem key={type._id}>
                        <ListItemText
                          primary={type._id}
                          secondary={`${type.count} subscriptions`}
                        />
                        <ListItemSecondaryAction>
                          <Typography variant="h6">
                            ${type.totalMonthlyRevenue?.toFixed(2) || '0.00'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            /month
                          </Typography>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Growth Metrics
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="New Subscriptions" />
                      <ListItemSecondaryAction>
                        <Typography variant="h6">
                          {analytics.growth?.newSubscriptions || 0}
                        </Typography>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Subscription Growth" />
                      <ListItemSecondaryAction>
                        <Typography variant="h6" color={analytics.growth?.subscriptionGrowth > 0 ? 'success.main' : 'error.main'}>
                          {analytics.growth?.subscriptionGrowth || 0}%
                        </Typography>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="MRR Growth" />
                      <ListItemSecondaryAction>
                        <Typography variant="h6" color={analytics.growth?.mrrGrowth > 0 ? 'success.main' : 'error.main'}>
                          {analytics.growth?.mrrGrowth || 0}%
                        </Typography>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Alert severity="info">
                  Advanced analytics and revenue forecasting features coming soon
                </Alert>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Invoices Tab */}
          <Alert severity="info">
            Invoice management and payment history coming soon
          </Alert>
        </TabPanel>
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {selectedSubscription?.status === 'active' && (
          <>
            <MenuItem onClick={() => {
              setAnchorEl(null);
              setPauseDialog(true);
            }}>
              <ListItemIcon>
                <PauseIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Pause Subscription</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              setAnchorEl(null);
              setCancelDialog(true);
            }}>
              <ListItemIcon>
                <CancelIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Cancel Subscription</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              setAnchorEl(null);
              setDiscountDialog(true);
            }}>
              <ListItemIcon>
                <DiscountIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Apply Discount</ListItemText>
            </MenuItem>
          </>
        )}
        
        {selectedSubscription?.status === 'paused' && (
          <MenuItem onClick={() => {
            setAnchorEl(null);
            handleResumeSubscription(selectedSubscription._id);
          }}>
            <ListItemIcon>
              <ResumeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Resume Subscription</ListItemText>
          </MenuItem>
        )}
        
        <MenuItem onClick={() => {
          setAnchorEl(null);
          setInvoiceDialog(true);
        }}>
          <ListItemIcon>
            <InvoiceIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Invoices</ListItemText>
        </MenuItem>
      </Menu>

      {/* Subscription Dialog */}
      <Dialog
        open={subscriptionDialog}
        onClose={() => setSubscriptionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedSubscription ? 'Edit Subscription' : 'Create Subscription'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                options={[]} // Would load customers from API
                renderInput={(params) => (
                  <TextField {...params} label="Customer" required />
                )}
                value={subscriptionForm.customer}
                onChange={(e, value) => setSubscriptionForm({ ...subscriptionForm, customer: value })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subscription Name"
                value={subscriptionForm.name}
                onChange={(e) => setSubscriptionForm({ ...subscriptionForm, name: e.target.value })}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={subscriptionForm.type}
                  label="Type"
                  onChange={(e) => setSubscriptionForm({ ...subscriptionForm, type: e.target.value })}
                >
                  <MenuItem value="Seasonal Site">Seasonal Site</MenuItem>
                  <MenuItem value="Monthly Site">Monthly Site</MenuItem>
                  <MenuItem value="Storage">Storage</MenuItem>
                  <MenuItem value="Membership">Membership</MenuItem>
                  <MenuItem value="WiFi Service">WiFi Service</MenuItem>
                  <MenuItem value="Cable Service">Cable Service</MenuItem>
                  <MenuItem value="Custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={subscriptionForm.description}
                onChange={(e) => setSubscriptionForm({ ...subscriptionForm, description: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider>Billing Details</Divider>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={subscriptionForm.billing.amount}
                onChange={(e) => setSubscriptionForm({
                  ...subscriptionForm,
                  billing: { ...subscriptionForm.billing, amount: parseFloat(e.target.value) || 0 }
                })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Billing Interval</InputLabel>
                <Select
                  value={subscriptionForm.billing.interval}
                  label="Billing Interval"
                  onChange={(e) => setSubscriptionForm({
                    ...subscriptionForm,
                    billing: { ...subscriptionForm.billing, interval: e.target.value }
                  })}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Tax Rate"
                type="number"
                value={subscriptionForm.billing.taxRate}
                onChange={(e) => setSubscriptionForm({
                  ...subscriptionForm,
                  billing: { ...subscriptionForm.billing, taxRate: parseFloat(e.target.value) || 0 }
                })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider>Schedule</Divider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Start Date"
                value={subscriptionForm.schedule.startDate}
                onChange={(value) => setSubscriptionForm({
                  ...subscriptionForm,
                  schedule: { ...subscriptionForm.schedule, startDate: value || dayjs() }
                })}
                sx={{ width: '100%' }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="End Date (Optional)"
                value={subscriptionForm.schedule.endDate}
                onChange={(value) => setSubscriptionForm({
                  ...subscriptionForm,
                  schedule: { ...subscriptionForm.schedule, endDate: value }
                })}
                sx={{ width: '100%' }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={subscriptionForm.trial.enabled}
                    onChange={(e) => setSubscriptionForm({
                      ...subscriptionForm,
                      trial: { ...subscriptionForm.trial, enabled: e.target.checked }
                    })}
                  />
                }
                label="Enable Trial Period"
              />
            </Grid>
            
            {subscriptionForm.trial.enabled && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Trial Days"
                  type="number"
                  value={subscriptionForm.trial.days}
                  onChange={(e) => setSubscriptionForm({
                    ...subscriptionForm,
                    trial: { ...subscriptionForm.trial, days: parseInt(e.target.value) || 0 }
                  })}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscriptionDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveSubscription} variant="contained">
            {selectedSubscription ? 'Update' : 'Create'} Subscription
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)}>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The customer will lose access to their subscription benefits.
          </Alert>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Cancellation Reason</InputLabel>
                <Select
                  value={cancelForm.reason}
                  label="Cancellation Reason"
                  onChange={(e) => setCancelForm({ ...cancelForm, reason: e.target.value })}
                >
                  <MenuItem value="too_expensive">Too Expensive</MenuItem>
                  <MenuItem value="no_longer_needed">No Longer Needed</MenuItem>
                  <MenuItem value="switching_provider">Switching Provider</MenuItem>
                  <MenuItem value="customer_request">Customer Request</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional Feedback"
                value={cancelForm.feedback}
                onChange={(e) => setCancelForm({ ...cancelForm, feedback: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <DatePicker
                label="Effective Date"
                value={cancelForm.effectiveDate}
                onChange={(value) => setCancelForm({ ...cancelForm, effectiveDate: value })}
                sx={{ width: '100%' }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)}>Cancel</Button>
          <Button onClick={handleCancelSubscription} variant="contained" color="error">
            Cancel Subscription
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pause Dialog */}
      <Dialog open={pauseDialog} onClose={() => setPauseDialog(false)}>
        <DialogTitle>Pause Subscription</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DatePicker
                label="Pause Until (Optional)"
                value={pauseForm.until}
                onChange={(value) => setPauseForm({ ...pauseForm, until: value })}
                sx={{ width: '100%' }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Reason for Pause"
                value={pauseForm.reason}
                onChange={(e) => setPauseForm({ ...pauseForm, reason: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseDialog(false)}>Cancel</Button>
          <Button onClick={handlePauseSubscription} variant="contained">
            Pause Subscription
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discount Dialog */}
      <Dialog open={discountDialog} onClose={() => setDiscountDialog(false)}>
        <DialogTitle>Apply Discount</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Discount Type</InputLabel>
                <Select
                  value={discountForm.type}
                  label="Discount Type"
                  onChange={(e) => setDiscountForm({ ...discountForm, type: e.target.value })}
                >
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="fixed">Fixed Amount</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Discount Value"
                type="number"
                value={discountForm.value}
                onChange={(e) => setDiscountForm({ ...discountForm, value: parseFloat(e.target.value) || 0 })}
                InputProps={{
                  startAdornment: discountForm.type === 'fixed' && <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: discountForm.type === 'percentage' && <InputAdornment position="end">%</InputAdornment>
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason"
                value={discountForm.reason}
                onChange={(e) => setDiscountForm({ ...discountForm, reason: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <DatePicker
                label="End Date (Optional)"
                value={discountForm.endDate}
                onChange={(value) => setDiscountForm({ ...discountForm, endDate: value })}
                sx={{ width: '100%' }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscountDialog(false)}>Cancel</Button>
          <Button onClick={handleApplyDiscount} variant="contained" startIcon={<DiscountIcon />}>
            Apply Discount
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BillingPage;